import { create } from 'zustand';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import {
  CommentThread,
  Comment,
  CommentReaction,
  CommentReactionType,
  CommentContextType,
  CreateCommentThreadRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  AddCommentReactionRequest,
} from '../shared/types/comments';
import { useAuthStore } from './authStore';

interface CommentState {
  // Threads by context
  threads: Record<string, CommentThread[]>; // key: `${contextType}:${contextId}`
  
  // Comments by thread ID
  comments: Record<number, Comment[]>;
  
  // Thread details
  threadDetails: Record<number, CommentThread>;
  
  // Loading states
  loadingThreads: Record<string, boolean>;
  loadingComments: Record<number, boolean>;
  creatingThread: boolean;
  creatingComment: Record<number, boolean>; // threadId -> boolean
  
  // Polling
  pollingIntervals: Record<string, NodeJS.Timeout | null>; // context key -> interval
  
  // Actions
  loadThreads: (contextType: CommentContextType, contextId: number) => Promise<void>;
  loadThread: (threadId: number) => Promise<void>;
  createThread: (data: CreateCommentThreadRequest) => Promise<CommentThread>;
  createComment: (data: CreateCommentRequest) => Promise<Comment>;
  updateComment: (commentId: number, data: UpdateCommentRequest) => Promise<void>;
  deleteComment: (commentId: number) => Promise<void>;
  addReaction: (commentId: number, reactionType: string) => Promise<void>;
  removeReaction: (commentId: number, reactionType: string) => Promise<void>;
  markThreadRead: (threadId: number) => Promise<void>;
  
  // Real-time polling
  startPolling: (contextType: CommentContextType, contextId: number) => void;
  stopPolling: (contextType: CommentContextType, contextId: number) => void;
  stopAllPolling: () => void;
  
  // Clear state
  clearContext: (contextType: CommentContextType, contextId: number) => void;
  clearAllCache: () => void;
}

const POLL_INTERVAL = 30000; // 30 seconds - reduced from 3s to prevent rate limiting

export const useCommentStore = create<CommentState>((set, get) => ({
  threads: {},
  comments: {},
  threadDetails: {},
  loadingThreads: {},
  loadingComments: {},
  creatingThread: false,
  creatingComment: {},
  pollingIntervals: {},

  loadThreads: async (contextType, contextId) => {
    const key = `${contextType}:${contextId}`;
    
    set((state) => ({
      loadingThreads: { ...state.loadingThreads, [key]: true },
    }));

    try {
      const response = await authApi.getCommentThreads(contextType, contextId);
      
      set((state) => ({
        threads: { ...state.threads, [key]: response.threads },
        loadingThreads: { ...state.loadingThreads, [key]: false },
      }));
    } catch (error: any) {
      logger.error('Error loading threads', error, 'comments');
      // Silently fail - don't show error to user if it's a network/backend issue
      set((state) => ({
        loadingThreads: { ...state.loadingThreads, [key]: false },
      }));
      // Don't throw - gracefully handle API failures
    }
  },

  loadThread: async (threadId) => {
    set((state) => ({
      loadingComments: { ...state.loadingComments, [threadId]: true },
    }));

    try {
      const response = await authApi.getCommentThread(threadId);
      
      // Build flat list of all comments (including nested)
      const flattenComments = (comments: Comment[]): Comment[] => {
        const result: Comment[] = [];
        comments.forEach((comment) => {
          result.push(comment);
          if (comment.replies && comment.replies.length > 0) {
            result.push(...flattenComments(comment.replies));
          }
        });
        return result;
      };

      const allComments = flattenComments(response.comments);
      
      set((state) => ({
        comments: { ...state.comments, [threadId]: allComments },
        threadDetails: { ...state.threadDetails, [threadId]: response.thread },
        loadingComments: { ...state.loadingComments, [threadId]: false },
      }));
    } catch (error: any) {
      logger.error('Error loading thread', error, 'comments');
      set((state) => ({
        loadingComments: { ...state.loadingComments, [threadId]: false },
      }));
      // Don't throw - gracefully handle API failures
    }
  },

  createThread: async (data) => {
    set({ creatingThread: true });

    try {
      const response = await authApi.createCommentThread(data);
      const thread = response.thread;
      
      const key = `${data.context_type}:${data.context_id}`;
      
      // Check if thread already exists in the list (in case API returned existing thread)
      set((state) => {
        const existingThreads = state.threads[key] || [];
        const threadExists = existingThreads.some(t => t.id === thread.id);
        
        return {
          threads: {
            ...state.threads,
            [key]: threadExists 
              ? existingThreads.map(t => t.id === thread.id ? thread : t) // Update existing
              : [...existingThreads, thread], // Add new
          },
          creatingThread: false,
        };
      });

      return thread;
    } catch (error: any) {
      logger.error('Error creating thread', error, 'comments');
      set({ creatingThread: false });
      // Don't throw - gracefully handle API failures
      throw error; // Still throw for create operations so UI can show feedback
    }
  },

  createComment: async (data) => {
    const threadId = data.thread_id;
    const tempId = -Date.now(); // Temporary negative ID
    const user = useAuthStore.getState().user;
    
    set((state) => ({
      creatingComment: { ...state.creatingComment, [threadId]: true },
    }));

    // Optimistically add comment to UI immediately
    const tempComment: Comment = {
      id: tempId,
      thread_id: threadId,
      parent_comment_id: data.parent_comment_id || null,
      content: data.content,
      created_by: user?.id || 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      deleted_at: null,
      edited_at: null,
      created_by_user: {
        id: user?.id || 0,
        email: user?.email || 'You',
        display_name: user?.display_name,
      },
      reactions: [],
      replies: [],
      mentions: [],
    };

    // Add optimistic comment to store BEFORE the API call
    set((state) => {
      const currentComments = state.comments[threadId] || [];
      return {
        comments: {
          ...state.comments,
          [threadId]: [...currentComments, tempComment],
        },
      };
    });

    try {
      // Create comment via API
      const response = await authApi.createComment(data);
      const comment = response.comment;
      
      // Replace temp comment with real comment (don't reload entire thread)
      set((state) => {
        const currentComments = state.comments[threadId] || [];
        return {
          comments: {
            ...state.comments,
            [threadId]: currentComments.map((c) => 
              c.id === tempId ? comment : c
            ),
          },
          creatingComment: { ...state.creatingComment, [threadId]: false },
        };
      });

      return comment;
    } catch (error: any) {
      // Remove optimistic comment on error
      set((state) => {
        const currentComments = state.comments[threadId] || [];
        return {
          comments: {
            ...state.comments,
            [threadId]: currentComments.filter((c) => c.id !== tempId),
          },
          creatingComment: { ...state.creatingComment, [threadId]: false },
        };
      });
      
      throw error;
    }
  },

  updateComment: async (commentId, data) => {
    try {
      await authApi.updateComment(commentId, data);
      
      // Find and update comment in store
      set((state) => {
        const newComments = { ...state.comments };
        Object.keys(newComments).forEach((threadIdStr) => {
          const threadId = parseInt(threadIdStr, 10);
          const comments = newComments[threadId];
          if (comments) {
            const updateCommentInTree = (comments: Comment[]): Comment[] => {
              return comments.map((c) => {
                if (c.id === commentId) {
                  return { ...c, ...data, edited_at: new Date().toISOString() };
                }
                if (c.replies && c.replies.length > 0) {
                  return { ...c, replies: updateCommentInTree(c.replies) };
                }
                return c;
              });
            };
            newComments[threadId] = updateCommentInTree(comments);
          }
        });
        return { comments: newComments };
      });
    } catch (error) {
      logger.error('Error updating comment', error, 'comments');
      throw error;
    }
  },

  deleteComment: async (commentId) => {
    try {
      await authApi.deleteComment(commentId);
      
      // Remove comment from store
      set((state) => {
        const newComments = { ...state.comments };
        Object.keys(newComments).forEach((threadIdStr) => {
          const threadId = parseInt(threadIdStr, 10);
          const comments = newComments[threadId];
          if (comments) {
            newComments[threadId] = comments.filter((c) => c.id !== commentId);
          }
        });
        return { comments: newComments };
      });
    } catch (error) {
      logger.error('Error deleting comment', error, 'comments');
      throw error;
    }
  },

  addReaction: async (commentId, reactionType) => {
    // Get current user for optimistic update
    const user = useAuthStore.getState().user;
    if (!user) {
      logger.error('Cannot add reaction: user not logged in', null, 'comments');
      return;
    }

    // Find the thread containing this comment
    const threadId = Object.keys(get().comments).find((tid) => {
      const comments = get().comments[parseInt(tid, 10)];
      return comments?.some((c) => c.id === commentId);
    });

    if (!threadId) {
      logger.error('Cannot find thread for comment', { commentId }, 'comments');
      return;
    }

    const parsedThreadId = parseInt(threadId, 10);

    // Create optimistic reaction
    const optimisticReaction: CommentReaction = {
      id: -Date.now(), // Temporary negative ID
      comment_id: commentId,
      user_id: user.id,
      reaction_type: reactionType as CommentReactionType,
      created_at: new Date().toISOString(),
      user: {
        id: user.id,
        email: user.email,
        display_name: user.display_name,
      },
    };

    // Optimistically add reaction to UI immediately
    set((state) => {
      const comments = state.comments[parsedThreadId] || [];
      return {
        comments: {
          ...state.comments,
          [parsedThreadId]: comments.map((c) => {
            if (c.id === commentId) {
              return {
                ...c,
                reactions: [...(c.reactions || []), optimisticReaction],
              };
            }
            return c;
          }),
        },
      };
    });

    try {
      // Make API call
      await authApi.addCommentReaction(commentId, { reaction_type: reactionType as CommentReactionType });
      // API succeeded - optimistic update stays (server will have the real ID, but we don't need it)
    } catch (error) {
      logger.error('Error adding reaction', error, 'comments');
      
      // Rollback optimistic update on error
      set((state) => {
        const comments = state.comments[parsedThreadId] || [];
        return {
          comments: {
            ...state.comments,
            [parsedThreadId]: comments.map((c) => {
              if (c.id === commentId) {
                return {
                  ...c,
                  reactions: (c.reactions || []).filter(
                    (r) => r.id !== optimisticReaction.id
                  ),
                };
              }
              return c;
            }),
          },
        };
      });
      
      throw error;
    }
  },

  removeReaction: async (commentId, reactionType) => {
    // Get current user for optimistic update
    const user = useAuthStore.getState().user;
    if (!user) {
      logger.error('Cannot remove reaction: user not logged in', null, 'comments');
      return;
    }

    // Find the thread containing this comment
    const threadId = Object.keys(get().comments).find((tid) => {
      const comments = get().comments[parseInt(tid, 10)];
      return comments?.some((c) => c.id === commentId);
    });

    if (!threadId) {
      logger.error('Cannot find thread for comment', { commentId }, 'comments');
      return;
    }

    const parsedThreadId = parseInt(threadId, 10);

    // Store the reaction being removed for potential rollback
    const currentComments = get().comments[parsedThreadId] || [];
    const targetComment = currentComments.find((c) => c.id === commentId);
    const removedReaction = targetComment?.reactions?.find(
      (r) => r.user_id === user.id && r.reaction_type === reactionType
    );

    // Optimistically remove reaction from UI immediately
    set((state) => {
      const comments = state.comments[parsedThreadId] || [];
      return {
        comments: {
          ...state.comments,
          [parsedThreadId]: comments.map((c) => {
            if (c.id === commentId) {
              return {
                ...c,
                reactions: (c.reactions || []).filter(
                  (r) => !(r.user_id === user.id && r.reaction_type === reactionType)
                ),
              };
            }
            return c;
          }),
        },
      };
    });

    try {
      // Make API call
      await authApi.removeCommentReaction(commentId, reactionType);
      // API succeeded - optimistic update stays
    } catch (error) {
      logger.error('Error removing reaction', error, 'comments');
      
      // Rollback optimistic update on error - re-add the reaction
      if (removedReaction) {
        set((state) => {
          const comments = state.comments[parsedThreadId] || [];
          return {
            comments: {
              ...state.comments,
              [parsedThreadId]: comments.map((c) => {
                if (c.id === commentId) {
                  return {
                    ...c,
                    reactions: [...(c.reactions || []), removedReaction],
                  };
                }
                return c;
              }),
            },
          };
        });
      }
      
      throw error;
    }
  },

  markThreadRead: async (threadId) => {
    try {
      await authApi.markThreadRead(threadId);
      
      // Update unread count
      set((state) => {
        const thread = state.threadDetails[threadId];
        if (thread) {
          return {
            threadDetails: {
              ...state.threadDetails,
              [threadId]: { ...thread, unread_count: 0 },
            },
          };
        }
        return state;
      });
    } catch (error) {
      logger.error('Error marking thread as read', error, 'comments');
      throw error;
    }
  },

  startPolling: (contextType, contextId) => {
    const key = `${contextType}:${contextId}`;
    
    // Stop existing polling if any
    get().stopPolling(contextType, contextId);
    
    // Start new polling
    const interval = setInterval(() => {
      const state = get();
      // Don't poll if we're currently creating a comment
      const isCreating = Object.values(state.creatingComment).some(v => v === true);
      if (!isCreating) {
        state.loadThreads(contextType, contextId);
      }
    }, POLL_INTERVAL);
    
    set((state) => ({
      pollingIntervals: { ...state.pollingIntervals, [key]: interval },
    }));
  },

  stopPolling: (contextType, contextId) => {
    const key = `${contextType}:${contextId}`;
    const interval = get().pollingIntervals[key];
    
    if (interval) {
      clearInterval(interval);
      set((state) => {
        const newIntervals = { ...state.pollingIntervals };
        delete newIntervals[key];
        return { pollingIntervals: newIntervals };
      });
    }
  },

  stopAllPolling: () => {
    Object.values(get().pollingIntervals).forEach((interval) => {
      if (interval) {
        clearInterval(interval);
      }
    });
    set({ pollingIntervals: {} });
  },

  clearContext: (contextType, contextId) => {
    const key = `${contextType}:${contextId}`;
    get().stopPolling(contextType, contextId);
    
    set((state) => {
      const newThreads = { ...state.threads };
      delete newThreads[key];
      return { threads: newThreads };
    });
  },

  clearAllCache: () => {
    // Clear all cached comments/threads to force fresh data on next load
    // Useful when user updates their profile (display name changes)
    get().stopAllPolling();
    set({
      threads: {},
      comments: {},
      threadDetails: {},
    });
  },
}));
